import { useState, type ComponentType, type ReactNode } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  styled,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  DataGrid,
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  ExportPrint,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ACTIONS_COLUMN_TYPE,
} from "@mui/x-data-grid";
import { deDE, enUS } from "@mui/x-data-grid/locales";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import TablePagination from "@mui/material/TablePagination";
import type {
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowId,
  GridRowModesModel,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import {
  DetailDataCard,
  DetailDataCardField,
} from "./detail/DetailResponsiveDataCards";
import { gridColumnToCardContent } from "./gridColumnCardContent";

const DELETE_CONFIRMATION_THRESHOLD = 1;

const StyledToolbarButton = styled(ToolbarButton)(({ theme }) => ({
  borderRadius: theme.spacing(0.7),
}));

interface DataTableToolbarProps {
  onSearchChange?: (value: string) => void;
  searchDebounceMs?: number;
  onImport?: (file: File) => void | Promise<void>;
  handleImportClick?: () => void;
  getExportableFields?: () => string[];
  selectable?: boolean;
  onDelete?: (selectedIds: GridRowSelectionModel) => Promise<void>;
  selectedRows?: GridRowSelectionModel;
  deleting?: boolean;
  handleDeleteClick?: () => void;
  enablePrint?: boolean;
  toolbarActions?: ReactNode;
}

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    onSearchChange?: DataTableToolbarProps["onSearchChange"];
    searchDebounceMs?: DataTableToolbarProps["searchDebounceMs"];
    onImport?: DataTableToolbarProps["onImport"];
    handleImportClick?: DataTableToolbarProps["handleImportClick"];
    getExportableFields?: DataTableToolbarProps["getExportableFields"];
    selectable?: DataTableToolbarProps["selectable"];
    onDelete?: DataTableToolbarProps["onDelete"];
    selectedRows?: DataTableToolbarProps["selectedRows"];
    deleting?: DataTableToolbarProps["deleting"];
    handleDeleteClick?: DataTableToolbarProps["handleDeleteClick"];
    enablePrint?: DataTableToolbarProps["enablePrint"];
    toolbarActions?: DataTableToolbarProps["toolbarActions"];
  }
}

function DataTableToolbar({
  onSearchChange,
  searchDebounceMs = 150,
  onImport,
  handleImportClick = () => {},
  getExportableFields = () => [],
  selectable = false,
  onDelete,
  selectedRows = { type: "include", ids: new Set<GridRowId>() },
  deleting = false,
  handleDeleteClick = () => {},
  enablePrint = false,
  toolbarActions,
}: DataTableToolbarProps) {
  const { t } = useTranslation();

  return (
    <Toolbar style={{ padding: "0 16px" }}>
      <QuickFilter
        {...(onSearchChange && {
          debounceMs: searchDebounceMs,
          expanded: true,
        })}
      >
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <TextField
              {...controlProps}
              inputRef={ref}
              aria-label={t("Search")}
              placeholder={t("Search")}
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label={t("Clear")}
                      >
                        <CancelIcon fontSize="small" color="primary" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                  ...controlProps.slotProps?.input,
                },
                ...controlProps.slotProps,
              }}
            />
          )}
        />
      </QuickFilter>
      <Box sx={{ flex: 1 }} />
      <ColumnsPanelTrigger render={<StyledToolbarButton />}>
        <ViewColumnIcon fontSize="small" color="primary" />
        <Typography
          variant="body2"
          color="primary"
          sx={{
            fontWeight: 600,
            marginLeft: 0.5,
            display: { xs: "none", sm: "block" },
          }}
        >
          {t("Columns")}
        </Typography>
      </ColumnsPanelTrigger>
      {!onSearchChange && (
        <FilterPanelTrigger render={<StyledToolbarButton />}>
          <FilterListIcon fontSize="small" color="primary" />
          <Typography
            variant="body2"
            color="primary"
            sx={{
              fontWeight: 600,
              marginLeft: 0.5,
              display: { xs: "none", sm: "block" },
            }}
          >
            {t("Filter")}
          </Typography>
        </FilterPanelTrigger>
      )}
      {onImport && (
        <StyledToolbarButton onClick={handleImportClick}>
          <FileUploadIcon fontSize="small" color="primary" />
          <Typography
            variant="body2"
            color="primary"
            sx={{
              fontWeight: 600,
              marginLeft: 0.5,
              display: { xs: "none", sm: "block" },
            }}
          >
            {t("Import")}
          </Typography>
        </StyledToolbarButton>
      )}
      <ExportCsv
        render={<StyledToolbarButton />}
        options={{ fields: getExportableFields() }}
      >
        <FileDownloadIcon fontSize="small" color="primary" />
        <Typography
          variant="body2"
          color="primary"
          sx={{
            fontWeight: 600,
            marginLeft: 0.5,
            display: { xs: "none", sm: "block" },
          }}
        >
          {t("Export")}
        </Typography>
      </ExportCsv>
      {enablePrint && (
        <ExportPrint render={<StyledToolbarButton />}>
          <PrintIcon fontSize="small" color="primary" />
          <Typography
            variant="body2"
            color="primary"
            sx={{
              fontWeight: 600,
              marginLeft: 0.5,
              display: { xs: "none", sm: "block" },
            }}
          >
            {t("Print")}
          </Typography>
        </ExportPrint>
      )}
      {toolbarActions}
      {selectable && onDelete && selectedRows.ids.size > 0 && (
        <>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ mx: 0.5 }}
          />
          <StyledToolbarButton onClick={handleDeleteClick} disabled={deleting}>
            <DeleteIcon fontSize="small" color="error" />
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: 600, marginLeft: 0.5 }}
            >
              {t("Delete")}
            </Typography>
          </StyledToolbarButton>
        </>
      )}
    </Toolbar>
  );
}

interface DataTableProps<T> {
  columns: GridColDef[];
  data: T[];
  loading?: boolean;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onRowClick?: (row: T) => void;
  selectedRows?: GridRowSelectionModel;
  onSelectionChange?: (selected: GridRowSelectionModel) => void;
  idField?: keyof T;
  selectable?: boolean;
  onDelete?: (selectedIds: GridRowSelectionModel) => Promise<void>;
  deleting?: boolean;
  paginationMode?: "client" | "server";
  isRowSelectable?: (params: GridRowParams) => boolean;
  /** Backoffice: single/multi via boolean. Reseller: 'single' | 'multiple'. Both accepted. */
  singleSelect?: boolean;
  selectionMode?: "single" | "multiple";
  onImport?: (file: File) => void | Promise<void>;
  importLoading?: boolean;
  renderImportDialog?: (props: {
    open: boolean;
    onClose: () => void;
    onFileSelect: (file: File) => void | Promise<void>;
    loading: boolean;
  }) => ReactNode;
  noRowsOverlay?: ComponentType<any>;
  noRowsOverlayProps?: Record<string, unknown>;
  onSearchChange?: (value: string) => void;
  searchDebounceMs?: number;
  enablePrint?: boolean;
  toolbarActions?: ReactNode;
  /** Extra actions column appended after `columns` (e.g. inline row-edit actions). */
  extraColumns?: GridColDef[];
  getRowClassName?: (row: T) => string;
  onRowClickGuard?: (row: T, rowId: GridRowId) => boolean;
  processRowUpdate?: (newRow: T, oldRow: T) => Promise<T>;
  onProcessRowUpdateError?: (error: unknown) => void;
  rowModesModel?: GridRowModesModel;
  onRowModesModelChange?: (model: GridRowModesModel) => void;
  editMode?: "row" | "cell";
  /** Enables the `< md` stacked-card mobile layout (reseller pattern). */
  enableMobileCards?: boolean;
  /** Merged into the DataGrid's own `sx` for app-specific row styling. */
  sx?: SxProps<Theme>;
}

function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  loading = false,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  selectedRows: externalSelectedRows,
  onSelectionChange,
  idField = "id" as keyof T,
  selectable = false,
  onDelete,
  deleting = false,
  paginationMode = "server",
  isRowSelectable: customIsRowSelectable,
  singleSelect = false,
  selectionMode,
  onImport,
  importLoading = false,
  renderImportDialog,
  noRowsOverlay: NoRowsOverlay,
  noRowsOverlayProps,
  onSearchChange,
  searchDebounceMs = 150,
  enablePrint = false,
  toolbarActions,
  extraColumns,
  getRowClassName,
  onRowClickGuard,
  processRowUpdate,
  onProcessRowUpdateError,
  rowModesModel,
  onRowModesModelChange,
  editMode,
  enableMobileCards = false,
  sx,
}: DataTableProps<T>) {
  const { t, i18n } = useTranslation();
  const gridLocale = i18n.language === "de" ? deDE : enUS;
  const [internalSelectedRows, setInternalSelectedRows] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set<GridRowId>(),
    });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [mobileFilter, setMobileFilter] = useState("");

  const selectedRows = externalSelectedRows ?? internalSelectedRows;
  const isSelectionControlled = externalSelectedRows !== undefined;
  const disableMultipleRowSelection =
    singleSelect || selectionMode === "single";

  const gridColumns: GridColDef[] = extraColumns
    ? [...columns, ...extraColumns]
    : columns;

  /** Let the last column grow so the grid uses the full container width. */
  const columnsWithFlex: GridColDef[] = (() => {
    if (!enableMobileCards) return gridColumns;
    if (gridColumns.length === 0) return gridColumns;
    if (gridColumns.some((c) => c.flex != null && Number(c.flex) > 0))
      return gridColumns;
    return gridColumns.map((col, index) => {
      if (index < gridColumns.length - 1) return col;
      const minW = col.minWidth ?? col.width ?? 100;
      const { width: _w, ...rest } = col;
      return { ...rest, flex: 1, minWidth: minW };
    });
  })();

  const cardColumns = columnsWithFlex.filter(
    (c) =>
      c.field !== GRID_CHECKBOX_SELECTION_FIELD &&
      c.field !== GRID_ACTIONS_COLUMN_TYPE,
  );

  const pageSlice = (() => {
    if (paginationMode === "client") {
      const start = page * rowsPerPage;
      return data.slice(start, start + rowsPerPage);
    }
    return data;
  })();

  const mobileVisibleRows = (() => {
    if (!enableMobileCards) return [];
    const q = mobileFilter.trim().toLowerCase();
    if (!q) return pageSlice;
    return pageSlice.filter((row) => {
      try {
        return JSON.stringify(row).toLowerCase().includes(q);
      } catch {
        return false;
      }
    });
  })();

  const paginationCount =
    paginationMode === "server" ? totalCount : data.length;

  const handlePaginationChange = (model: GridPaginationModel) => {
    if (model.pageSize !== rowsPerPage) {
      localStorage.setItem("rowsPerPage", String(model.pageSize));
      onRowsPerPageChange(model.pageSize);
      if (page !== 0) onPageChange(0);
    } else if (model.page !== page) {
      onPageChange(model.page);
    }
  };

  const handleRowSelectionChange = (
    newSelection: GridRowSelectionModel | null,
  ) => {
    const empty: GridRowSelectionModel = { type: "include", ids: new Set() };
    if (!newSelection) {
      if (!isSelectionControlled) setInternalSelectedRows(empty);
      onSelectionChange?.(empty);
      return;
    }
    if (!isSelectionControlled) setInternalSelectedRows(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleDeleteClick = () => {
    if (selectedRows.ids.size === 0) return;
    if (selectedRows.ids.size >= DELETE_CONFIRMATION_THRESHOLD) {
      setDeleteDialogOpen(true);
    } else {
      handleDeleteConfirm();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete || selectedRows.ids.size === 0) return;
    try {
      await onDelete(selectedRows);
      if (!isSelectionControlled)
        setInternalSelectedRows({ type: "include", ids: new Set() });
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  const handleRowClickEvent = (params: GridRowParams) => {
    if (!onRowClick || !params?.row) return;
    const row = params.row as T;
    const rowId = params.id as GridRowId;
    if (onRowClickGuard && !onRowClickGuard(row, rowId)) return;
    onRowClick(row);
  };

  const getExportableFields = (): string[] => {
    const excludedFields = [
      GRID_ACTIONS_COLUMN_TYPE,
      GRID_CHECKBOX_SELECTION_FIELD,
    ];
    return gridColumns
      .filter((column) => !excludedFields.includes(column.field))
      .map((column) => column.field);
  };

  const handleImportClick = () => setImportDialogOpen(true);

  const handleImportFile = async (file: File) => {
    if (!onImport) return;
    try {
      await onImport(file);
      setImportDialogOpen(false);
    } catch (error) {
      console.error("Import error:", error);
    }
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    if (!onSearchChange) return;
    const quickFilterText = model.quickFilterValues?.join(" ") ?? "";
    onSearchChange(quickFilterText);
  };

  return (
    <Paper
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxHeight: { xs: "calc(100vh - 160px)", sm: "calc(100vh - 240px)" },
        overflow: "hidden",
      }}
    >
      {enableMobileCards && (
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <TextField
            size="small"
            fullWidth
            value={mobileFilter}
            onChange={(e) => setMobileFilter(e.target.value)}
            placeholder={t("Search")}
            aria-label={t("Search")}
            sx={{ flexShrink: 0, mx: 1.5, mt: 1.5, mb: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="primary" />
                  </InputAdornment>
                ),
                endAdornment: mobileFilter ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={t("Clear")}
                      onClick={() => setMobileFilter("")}
                      edge="end"
                    >
                      <CancelIcon fontSize="small" color="primary" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: 1.5,
              pb: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {loading && mobileVisibleRows.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : null}
            {!loading && mobileVisibleRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t("NoDataFound")}
              </Typography>
            ) : null}
            {mobileVisibleRows.map((row) => {
              const rid = row[idField] as GridRowId;
              return (
                <DetailDataCard
                  key={String(rid)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {cardColumns.map((col) => (
                    <DetailDataCardField
                      key={col.field}
                      label={col.headerName ?? col.field}
                    >
                      {gridColumnToCardContent(col, row, rid)}
                    </DetailDataCardField>
                  ))}
                </DetailDataCard>
              );
            })}
          </Box>
          <TablePagination
            component="div"
            count={paginationCount}
            page={page}
            onPageChange={(_, p) => onPageChange(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) =>
              onRowsPerPageChange(parseInt(e.target.value, 10))
            }
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={t("RowsPerPage")}
            sx={{
              flexShrink: 0,
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
                pl: 1,
                pr: 0.5,
              },
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: enableMobileCards ? { xs: "none", md: "flex" } : "flex",
          flex: 1,
          minHeight: 0,
          flexDirection: "column",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <DataGrid
          disableMultipleRowSelection={disableMultipleRowSelection}
          rows={data}
          columns={columnsWithFlex}
          loading={loading}
          getRowId={(row) => row[idField] as string | number}
          paginationModel={{ page, pageSize: rowsPerPage }}
          onPaginationModelChange={handlePaginationChange}
          {...(paginationMode === "server" && { rowCount: totalCount })}
          paginationMode={paginationMode}
          {...(onSearchChange && {
            filterMode: "server" as const,
            disableColumnFilter: true,
            onFilterModelChange: handleFilterModelChange,
          })}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection={selectable}
          {...(selectable && {
            rowSelectionModel: selectedRows ?? {
              type: "include" as const,
              ids: new Set<GridRowId>(),
            },
            onRowSelectionModelChange: handleRowSelectionChange,
          })}
          showCellVerticalBorder
          showColumnVerticalBorder
          {...(customIsRowSelectable && {
            isRowSelectable: customIsRowSelectable,
          })}
          onRowClick={handleRowClickEvent}
          showToolbar
          slots={{
            toolbar: DataTableToolbar,
            ...(NoRowsOverlay && { noRowsOverlay: NoRowsOverlay }),
          }}
          slotProps={{
            toolbar: {
              onSearchChange,
              searchDebounceMs,
              onImport,
              handleImportClick,
              getExportableFields,
              selectable,
              onDelete,
              selectedRows,
              deleting,
              handleDeleteClick,
              enablePrint,
              toolbarActions,
            },
            pagination: { labelRowsPerPage: t("RowsPerPage") },
            ...(NoRowsOverlay && { noRowsOverlay: noRowsOverlayProps }),
            columnsManagement: {
              getTogglableColumns: (allColumns) => {
                const hiddenFields = [
                  GRID_ACTIONS_COLUMN_TYPE,
                  GRID_CHECKBOX_SELECTION_FIELD,
                ];
                return allColumns
                  .filter((column) => !hiddenFields.includes(column.field))
                  .map((column) => column.field);
              },
            },
          }}
          localeText={{
            ...gridLocale.components.MuiDataGrid.defaultProps.localeText,
            noRowsLabel: t("NoDataFound"),
          }}
          {...(processRowUpdate && {
            editMode: editMode ?? "row",
            rowModesModel,
            onRowModesModelChange,
            processRowUpdate,
            onProcessRowUpdateError,
          })}
          {...(getRowClassName && {
            getRowClassName: (params: GridRowParams) =>
              getRowClassName(params.row as T),
          })}
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            border: "none",
            "& .MuiDataGrid-main": { width: "100%" },
            "& .MuiDataGrid-columnHeaders": (theme) => ({
              backgroundColor: "#F8FAFC",
              borderBottom: "2px solid #E2E8F0",
              ...theme.applyStyles("dark", {
                backgroundColor: "#111c30",
                borderBottom: "2px solid #334155",
              }),
            }),
            "& .MuiDataGrid-columnHeader": (theme) => ({
              fontWeight: 600,
              backgroundColor: "#F8FAFC",
              color: "#1E293B",
              ...theme.applyStyles("dark", {
                backgroundColor: "#111c30",
                color: "#f8fafc",
              }),
            }),
            "& .MuiDataGrid-columnHeaderTitle": (theme) => ({
              fontWeight: 600,
              ...theme.applyStyles("dark", {
                color: "#f8fafc",
              }),
            }),
            "& .MuiDataGrid-row:hover": {
              cursor: onRowClick ? "pointer" : "default",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            ...sx,
          }}
        />
      </Box>

      {selectable && onDelete && (
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          message={
            selectedRows.ids.size > DELETE_CONFIRMATION_THRESHOLD
              ? t("DeleteDataOverviewRows") + ` (${selectedRows.ids.size})`
              : t("DeleteDataOverviewRows")
          }
        />
      )}

      {onImport &&
        renderImportDialog?.({
          open: importDialogOpen,
          onClose: () => setImportDialogOpen(false),
          onFileSelect: handleImportFile,
          loading: importLoading,
        })}
    </Paper>
  );
}

export default DataTable;
export type { DataTableProps };
